/**
 * useProfileId — resolves Clerk user id → Supabase `profiles.id` (UUID).
 *
 * Direct DB writes through RLS-scoped queries don't need this (auth.uid()
 * already returns the profile UUID). It exists for paths that take the UUID
 * directly — e.g. Storage bucket folder name for avatar uploads.
 */

import { useQuery } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export function profileIdQueryKey(clerkId: string | null | undefined) {
  return ["profile-id-by-clerk", clerkId ?? "anon"] as const;
}

export interface UseProfileIdResult {
  profileId: string | null;
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useProfileId(): UseProfileIdResult {
  const { client, isReady: supabaseReady } = useSupabaseClient();
  const { user } = useCurrentUser();
  const clerkId = user?.id ?? null;

  const query = useQuery<string | null>({
    queryKey: profileIdQueryKey(clerkId),
    enabled: supabaseReady && Boolean(clerkId),
    staleTime: Infinity,
    queryFn: async () => {
      if (!clerkId) return null;
      const { data, error } = await client
        .from("profiles")
        .select("id")
        .eq("clerk_id", clerkId)
        .maybeSingle();
      if (error) throw error;
      return (data?.id as string | undefined) ?? null;
    },
  });

  return {
    profileId: query.data ?? null,
    isReady: supabaseReady && !!query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
